import React, { useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FormValues, TranslationType } from "../types";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
} from "react-hook-form";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import * as i18nIsoCountries from "i18n-iso-countries";
// Import the languages you wish to support
import en from "i18n-iso-countries/langs/en.json";
import de from "i18n-iso-countries/langs/de.json";
import fr from "i18n-iso-countries/langs/fr.json";
import nl from "i18n-iso-countries/langs/nl.json";

// Register the locales you want to support. Must cover every booking language —
// a missing locale makes getNames() return {} and the country dropdown renders
// empty (only the "all countries" label), which is what broke the NL flow.
i18nIsoCountries.registerLocale(en);
i18nIsoCountries.registerLocale(de);
i18nIsoCountries.registerLocale(fr);
i18nIsoCountries.registerLocale(nl);

// This is your list of prioritized countries.
// The dropdown will now display them in THIS EXACT ORDER.
const PREFERRED_COUNTRY_CODES = [
  "CH",
  "LI",
  "DE",
  "AT",
  "FR",
  "IT",
  "GB",
  "NL",
];

interface AddressFieldsProps {
  register: UseFormRegister<FormValues>;
  errors: FieldErrors<FormValues>;
  t: TranslationType;
  control: Control<FormValues>;
  language?: string;
  /** Default country ISO-2 (upper-case) — driven by the active market. */
  defaultCountry?: string;
}

export const AddressFields = ({
  register,
  errors,
  t,
  control,
  language = "en",
  defaultCountry = "CH",
}: AddressFieldsProps) => {
  const { preferredCountries, otherCountries } = useMemo(() => {
    const langCode = language.slice(0, 2);
    // "alias" picks the common short name over the official one — "Nederland"
    // rather than "Koninkrijk der Nederlanden" (AHD-8731).
    const allCountryNames = i18nIsoCountries.getNames(langCode, {
      select: "alias",
    });

    // The active market's country leads the list (NL first in the NL flow, DE
    // first in the DE flow), then the rest of the preferred codes in their
    // fixed order.
    const orderedCodes = [
      defaultCountry,
      ...PREFERRED_COUNTRY_CODES.filter((code) => code !== defaultCountry),
    ];

    const preferredCountries = orderedCodes
      .map((code) => {
        const name = allCountryNames[code];
        return name ? { code, name } : null;
      })
      .filter(Boolean); // remove any nulls if a code was not found

    const preferredCodesSet = new Set(orderedCodes);
    const otherCountries = Object.entries(allCountryNames)
      .filter(([code]) => !preferredCodesSet.has(code))
      .map(([code, name]) => ({ code, name }))
      .sort((a, b) => a.name.localeCompare(b.name)); // Sort the rest alphabetically

    return { preferredCountries, otherCountries };
  }, [language, defaultCountry]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="street">{t.street}</Label>
          <Input
            id="street"
            {...register("street", { required: true })}
            className={errors.street ? "border-red-500" : ""}
            autoComplete="address-line1"
          />
          {errors.street && (
            <span className="text-sm text-red-500">{t.streetRequired}</span>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="streetNumber">{t.streetNumber}</Label>
          <Input
            id="streetNumber"
            {...register("streetNumber", { required: true })}
            className={errors.streetNumber ? "border-red-500" : ""}
            autoComplete="off"
          />
          {errors.streetNumber && (
            <span className="text-sm text-red-500">
              {t.streetNumberRequired}
            </span>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-6 gap-4">
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="zipCode">{t.zipCode}</Label>
          <Input
            id="zipCode"
            {...register("zipCode", { required: true })}
            className={errors.zipCode ? "border-red-500" : ""}
            autoComplete="postal-code"
          />
          {errors.zipCode && (
            <span className="text-sm text-red-500">{t.zipCodeRequired}</span>
          )}
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="city">{t.city}</Label>
          <Input
            id="city"
            {...register("city", { required: true })}
            className={errors.city ? "border-red-500" : ""}
            autoComplete="address-level2"
          />
          {errors.city && (
            <span className="text-sm text-red-500">{t.cityRequired}</span>
          )}
        </div>
        {/* Country */}
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="country">{t.country}</Label>
          <Controller
            name="country"
            control={control}
            rules={{ required: true }}
            defaultValue={defaultCountry}
            render={({ field }) => {
              return (
                <Select
                  key={language}
                  value={field.value}
                  onValueChange={field.onChange}
                >
                  <SelectTrigger
                    className={`py-2 ${errors.country ? "border-red-500" : ""}`}
                    id="country"
                  >
                    <SelectValue placeholder={t.selectCountry} />
                  </SelectTrigger>
                  <SelectContent>
                    {preferredCountries.length > 0 && (
                      <SelectGroup>
                        {/* CHANGE: Removed the <SelectLabel> from here */}
                        {preferredCountries.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                    {/* The separator remains to visually divide the groups */}
                    <hr className="my-1" />
                    <SelectGroup>
                      <SelectLabel>{t.allCountries}</SelectLabel>
                      {otherCountries.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              );
            }}
          />
          {errors.country && (
            <span className="text-sm text-red-500">{t.countryRequired}</span>
          )}
        </div>
      </div>
    </div>
  );
};
